import {useLocationState} from '@raison/navigation';
import {debounce} from '@raison/toolbox';
import {RsnButton, RsnDialog, useBoolean} from '@raison/ui-kit';
import * as Schema from 'effect/Schema';
import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router';
import {CommonLoaderDialog} from '~/entities/dialogs';
import {
    cancelPendingBiometryActivation,
    get_biometry_type,
    get_touch_id_availability,
    gonative_auth,
    gonative_save_secret,
} from '~/entities/user';
import {appLocalStorage} from '~/shared/persistence';
import {getBiometryHelper} from '~/shared/utils/biometryHelper';
import {getSavePassHandler} from '~/shared/utils/savePass';
import {BiometryHead} from './BiometryHead/BiometryHead';
import {LoginBiometryStatusModal} from './LoginBiometryStatusDialog';

const biometryHelper = getBiometryHelper();
const savePassHandler = getSavePassHandler();

interface BiometryScreenProps {
    password: string;
    callPoint: 'loginForm' | 'signupForm' | 'forgotPasswordForm';
    onSuccess: () => void;
    onLater: () => void;
}

export function BiometrySetupScreen({password, onSuccess, onLater, callPoint}: BiometryScreenProps) {
    const navigate = useNavigate();
    const [dialog, setDialog] = useLocationState('biometry-setup-dialog', Dialog);
    const closeDialog = () => navigate(-1);

    const [biometryStatus, setBiometryStatus] = useState<{
        title: string;
        context: string;
        isError?: boolean;
    }>();

    const [loading, setLoading] = useBoolean(false);

    const [attemptNumber, setAttemptNumber] = useState(1);
    const [attemptFlag, setAttemptFlag] = useState(false);

    const biometryType = get_biometry_type() === 'faceId' ? 'Face ID' : 'Touch ID';

    const activateLater = () => {
        appLocalStorage.biometryStatus.set('Later');

        onLater();
    };

    const debouncedLoginByBiometry = debounce(gonative_auth, 300);

    const openActBiometryModal = () => {
        setTimeout(() => {
            const isSaveSecretError = appLocalStorage.saveSecretError.get() === 'true';
            if (isSaveSecretError) {
                setBiometryStatus({
                    title: 'Something went wrong',
                    context: `We're sorry, but we couldn't activate biometrics. Don't worry, we'll fix it. Please try activating again later in the “Settings” section.`,
                    isError: true,
                });
            } else {
                setBiometryStatus({
                    title: `${biometryType} is activated`,
                    context: `Raison app uses system biometrics.`,
                    isError: false,
                });
            }
            setDialog(Dialogs.biometryStatus);
        }, 500);
    };

    useEffect(() => {
        const disposeActBiometryModal = biometryHelper.setActBiometryModalLogin(openActBiometryModal);
        // a regular fail lets the user try again, only a lockout is worth a dialog
        const disposeOnBiometryFailed = biometryHelper.setOnBiometryFailed(reason => {
            // biometry is reset on a lockout, so the user just leaves the screen
            if (reason === 'lockout') onLater();
        });

        return () => {
            disposeActBiometryModal();
            disposeOnBiometryFailed();
            // the user may try again after a regular fail, but must not leave an unconfirmed secret behind
            cancelPendingBiometryActivation();
        };
    }, []);

    const activateBiometry = () => {
        setLoading.on();
        // if biometry is not available
        get_touch_id_availability(); // It takes some time (500ms)

        setTimeout(() => {
            if ((attemptNumber === 2 && !appLocalStorage.hasTouchId.get()) || attemptFlag) {
                setAttemptFlag(true);
                setAttemptNumber(attemptNumber + 1);

                setBiometryStatus({
                    title: 'Something went wrong',
                    context: `We're sorry, but we couldn't activate biometrics. Don't worry, we'll fix it. Please try activating again later in the “Settings” section.`,
                    isError: true,
                });
                setLoading.off();
                setDialog(Dialogs.biometryStatus);
                return;
            } else {
                setAttemptNumber(attemptNumber + 1);

                savePassHandler.setPassword(password);
                gonative_save_secret();
                biometryHelper.setRunAuth(false);
                appLocalStorage.callPointBiometry.set(callPoint);
                appLocalStorage.runBiometryActivation.set('start');
                debouncedLoginByBiometry();
                setLoading.off();
            }
        }, 500);
    };

    return (
        <div className={'flex flex-1 flex-col'}>
            <BiometryHead
                renderTitle={name => `Use ${name}`}
                renderSubtitle={name => `Activate ${name}, so you don’t need to enter your password every time.`}
            />

            <div className='flex flex-col gap-2 p-4'>
                <RsnButton.Primary onClick={activateBiometry} disabled={loading}>
                    Activate now
                </RsnButton.Primary>
                <RsnButton.Secondary onClick={activateLater} disabled={loading}>
                    Later
                </RsnButton.Secondary>
            </div>
            <RsnDialog.Controller onClose={closeDialog}>
                {(() => {
                    switch (true) {
                        case dialog === Dialogs.biometryStatus && !!biometryStatus: {
                            return (
                                <LoginBiometryStatusModal
                                    {...biometryStatus}
                                    onClose={() => {
                                        if (!biometryStatus.isError) {
                                            onSuccess();
                                        }
                                    }}
                                />
                            );
                        }
                    }
                })()}
            </RsnDialog.Controller>
            <RsnDialog.Controller>
                {loading && <CommonLoaderDialog reason={'Activating biometrics...'} />}
            </RsnDialog.Controller>
        </div>
    );
}

const Dialogs = {
    biometryStatus: 'biometry-status',
} as const;
const Dialog = Schema.Literal(...Object.values(Dialogs));
